import { useUser } from "../pages/login/UserContextPersistent";
import { isWriteAction } from "../layout/navLayoutUtils";

export const usePermission = () => {
  const { state, organizationInactive } = useUser();

  const hasPermission = (api_path, action) => {
    if (!state || !Array.isArray(state.features)) {
      return false;
    }
    if (organizationInactive && isWriteAction(action)) {
      return false;
    }
    return state.features.some((feature) => {
      const featPath =
        feature.api_path || feature.apiPath || feature.APIPath || "";
      return (
        (featPath === api_path || api_path === "") &&
        (feature.action === action || action === "")
      );
    });
  };

  const getSingularModuleName = (moduleName) => {
    let raw = String(moduleName || "").toLowerCase().trim();
    raw = raw.replace(/^(school|fleet)-/, "");
    raw = raw.replace(/_list\.csv$/, "").replace(/\.csv$/, "");
    if (
      raw.includes("entry-exit") ||
      raw.includes("entryexit") ||
      raw.includes("vehicle-log")
    ) {
      return "vehicle-entry-exit";
    }
    if (raw.includes("monitor-stop") || raw.includes("stop-monitor")) {
      return "monitor-stop";
    }
    if (
      raw.includes("speed") ||
      raw.includes("incident") ||
      raw.includes("overspeed")
    ) {
      return "speed-incident";
    }
    if (raw.endsWith("roles") || raw === "role" || raw.includes("role")) {
      return "role";
    }
    if (raw === "trips" || raw.includes("trip")) return "trip";
    if (raw.endsWith("schedules") || raw === "schedule") return "schedule";
    if (raw.endsWith("routes")) return "route";
    if (raw.endsWith("devices") || raw === "device") return "device";
    if (raw.endsWith("vehicles") || raw.includes("vehicle")) return "vehicle";
    if (raw.endsWith("stops") || raw.includes("stop")) return "stop";
    if (raw.endsWith("sites")) return "site";
    if (raw.endsWith("users")) return "user";
    if (raw.endsWith("drivers")) return "driver";
    if (raw.endsWith("students") || raw.includes("student")) return "student";
    if (raw.endsWith("attendants") || raw.endsWith("employees")) return "attendant";
    if (raw.endsWith("s")) return raw.slice(0, -1);
    return raw;
  };

  const hasDownloadPermission = (moduleName, defaultApiPath = "") => {
    if (!state || !Array.isArray(state.features)) {
      return false;
    }

    if (defaultApiPath && hasPermission(defaultApiPath, "GET")) {
      return true;
    }

    const singularMod = getSingularModuleName(moduleName);

    return state.features.some((feature) => {
      const nameLower = String(feature.name || "").toLowerCase();
      const displayLower = String(feature.displayName || "").toLowerCase();
      const pathLower = String(
        feature.api_path || feature.apiPath || feature.APIPath || "",
      ).toLowerCase();

      const isDownload =
        nameLower.includes("download") ||
        displayLower.includes("download") ||
        pathLower.includes("download");

      if (!isDownload) return false;

      if (singularMod === "vehicle-entry-exit") {
        return (
          nameLower.includes("entry") ||
          displayLower.includes("entry") ||
          pathLower.includes("entry") ||
          pathLower.includes("vehicle-log") ||
          nameLower.includes("vehicle") ||
          displayLower.includes("vehicle") ||
          pathLower.includes("vehicle")
        );
      }

      if (singularMod === "monitor-stop") {
        return (
          nameLower.includes("monitor") ||
          displayLower.includes("monitor") ||
          pathLower.includes("monitor") ||
          nameLower.includes("stop") ||
          displayLower.includes("stop") ||
          pathLower.includes("stop")
        );
      }

      if (singularMod === "speed-incident") {
        return (
          nameLower.includes("speed") ||
          displayLower.includes("speed") ||
          pathLower.includes("speed") ||
          nameLower.includes("incident") ||
          displayLower.includes("incident") ||
          pathLower.includes("incident") ||
          nameLower.includes("vehicle") ||
          displayLower.includes("vehicle") ||
          pathLower.includes("vehicle")
        );
      }

      if (singularMod === "vehicle") {
        const isEntryExit =
          nameLower.includes("entry") ||
          displayLower.includes("entry") ||
          pathLower.includes("vehicle-log");
        const isSpeedIncident =
          nameLower.includes("speed") ||
          displayLower.includes("speed") ||
          pathLower.includes("speed") ||
          nameLower.includes("incident") ||
          displayLower.includes("incident") ||
          pathLower.includes("incident");
        return (
          !isEntryExit &&
          !isSpeedIncident &&
          (nameLower.includes("vehicle") ||
            displayLower.includes("vehicle") ||
            pathLower.includes("vehicle"))
        );
      }

      if (singularMod === "stop") {
        const isMonitor =
          nameLower.includes("monitor") ||
          displayLower.includes("monitor") ||
          pathLower.includes("monitor");
        return (
          !isMonitor &&
          (nameLower.includes("stop") ||
            displayLower.includes("stop") ||
            pathLower.includes("stop"))
        );
      }

      if (singularMod === "trip") {
        return (
          nameLower.includes("trip") ||
          displayLower.includes("trip") ||
          pathLower.includes("trip")
        );
      }

      if (singularMod === "schedule") {
        const isTrip =
          nameLower.includes("trip") ||
          displayLower.includes("trip") ||
          pathLower.includes("trip");
        return (
          !isTrip &&
          (nameLower.includes("schedule") ||
            displayLower.includes("schedule") ||
            pathLower.includes("schedule"))
        );
      }

      if (singularMod === "device") {
        return (
          nameLower.includes("device") ||
          displayLower.includes("device") ||
          pathLower.includes("device")
        );
      }

      if (singularMod === "role") {
        return pathLower.includes("/api/role/download");
      }

      if (singularMod === "user") {
        const isRole =
          nameLower.includes("role") ||
          displayLower.includes("role") ||
          pathLower.includes("role");
        return (
          !isRole &&
          (nameLower.includes("user") ||
            displayLower.includes("user") ||
            pathLower.includes("user"))
        );
      }

      if (singularMod === "driver") {
        return (
          nameLower.includes("driver") ||
          displayLower.includes("driver") ||
          pathLower.includes("driver")
        );
      }

      if (singularMod === "route") {
        return (
          nameLower.includes("route") ||
          displayLower.includes("route") ||
          pathLower.includes("route")
        );
      }

      const isModuleMatch =
        !singularMod ||
        nameLower.includes(singularMod) ||
        displayLower.includes(singularMod) ||
        pathLower.includes(singularMod) ||
        (singularMod.includes("attendant") &&
          (nameLower.includes("employee") ||
            displayLower.includes("employee") ||
            pathLower.includes("employee")));

      return isModuleMatch;
    });
  };

  return { hasPermission, hasDownloadPermission };
};
